import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

export const TERMINAL_STORAGE_KEY = "portcall.current_terminal_id";

const TerminalContext = createContext(null);

export function TerminalProvider({ children }) {
  const [terminals, setTerminals] = useState([]);
  const [currentTerminalId, setCurrentTerminalIdState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refreshTerminals = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await api.list("terminals");
      setTerminals(rows);
      const stored = window.localStorage.getItem(TERMINAL_STORAGE_KEY);
      const selected = rows.some(item => item.id === stored)
        ? stored
        : rows.some(item => item.id === currentTerminalId)
          ? currentTerminalId
          : rows[0]?.id || null;
      setCurrentTerminalIdState(selected);
      if (selected) window.localStorage.setItem(TERMINAL_STORAGE_KEY, selected);
      else window.localStorage.removeItem(TERMINAL_STORAGE_KEY);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Unable to load terminals");
    } finally {
      setLoading(false);
    }
  }, [currentTerminalId]);

  useEffect(() => { refreshTerminals(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setCurrentTerminalId = useCallback((terminalId) => {
    if (!terminals.some(item => item.id === terminalId)) return false;
    setCurrentTerminalIdState(terminalId);
    window.localStorage.setItem(TERMINAL_STORAGE_KEY, terminalId);
    return true;
  }, [terminals]);

  const currentTerminal = useMemo(
    () => terminals.find(item => item.id === currentTerminalId) || null,
    [terminals, currentTerminalId],
  );

  const value = useMemo(() => ({
    terminals,
    currentTerminal,
    currentTerminalId,
    setCurrentTerminalId,
    refreshTerminals,
    loading,
    error,
  }), [terminals, currentTerminal, currentTerminalId, setCurrentTerminalId, refreshTerminals, loading, error]);

  return <TerminalContext.Provider value={value}>{children}</TerminalContext.Provider>;
}

export function useTerminal() {
  const value = useContext(TerminalContext);
  if (!value) throw new Error("useTerminal must be used inside TerminalProvider");
  return value;
}
